import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useGoogleLogin } from '@react-oauth/google';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const savedUser = sessionStorage.getItem('user');
    
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Nếu có user data đã lưu, restore ngay lập tức
      if (savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setLoading(false);
        } catch (error) {
          console.error('Error parsing saved user data:', error);
          sessionStorage.removeItem('user');
        }
      }
      
      // Sau đó check auth để cập nhật thông tin mới nhất
      checkAuth();
    } else if (sessionStorage.getItem('googleUser')) {
      // Restore Google user session
      try {
        const googleUser = JSON.parse(sessionStorage.getItem('googleUser'));
        setUser(googleUser);
      } catch (error) {
        console.error('Error parsing Google user data:', error);
        sessionStorage.removeItem('googleUser');
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      
      if (response.data.success) {
        const userData = response.data.data;
        setUser(userData);
        // Lưu user data vào sessionStorage
        sessionStorage.setItem('user', JSON.stringify(userData));
      } else {
        // API trả về error
        console.error('Auth check failed:', response.data);
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } catch (error) {
      console.error('Check auth error:', error);
      
      // Xử lý các loại lỗi khác nhau
      if (error.response) {
        const status = error.response.status;
        
        switch (status) {
          case 401:
            // Token không hợp lệ hoặc hết hạn
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            break;
          case 403:
            // Không có quyền truy cập
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            break;
          case 404:
            // API không tồn tại
            console.error('API /auth/me không tồn tại');
            break;
          case 500:
            // Server error
            console.error('Server error khi kiểm tra authentication');
            break;
          default:
            // Lỗi khác
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
        }
      } else if (error.request) {
        // Network error - không thể kết nối đến server
        console.error('Không thể kết nối đến server');
        // Không xóa token nếu là network error, có thể server tạm thời không khả dụng
      } else {
        // Lỗi khác
                    sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.success) {
        const { token, user } = response.data.data;
        
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(user);
        
        toast.success('Đăng nhập thành công!');
        
        // Xử lý điều hướng dựa trên trạng thái user
        handleUserNavigation(user);
      } else {
        toast.error(response.data.message || 'Đăng nhập thất bại');
        throw new Error(response.data.message || 'Đăng nhập thất bại');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            toast.error(data?.message || 'Email hoặc mật khẩu không đúng');
            break;
          case 401:
            toast.error(data?.message || 'Thông tin đăng nhập không hợp lệ');
            break;
          case 403:
            toast.error(data?.message || 'Tài khoản chưa được phê duyệt');
            break;
          case 404:
            toast.error('API không tồn tại');
            break;
          case 500:
            toast.error('Server đang gặp sự cố, vui lòng thử lại sau');
            break;
          default:
            toast.error(data?.message || 'Đăng nhập thất bại');
        }
      } else if (error.request) {
        // Network error - không thể kết nối đến server
        toast.error('Không thể kết nối đến server, vui lòng kiểm tra kết nối mạng');
      } else {
        // Lỗi khác
        toast.error('Đăng nhập thất bại');
      }
      
      throw error;
    }
  };



  // Google OAuth login - chỉ real mode
  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        // Get user info from Google
        const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${response.access_token}` },
        }).then(res => res.json());

        console.log('Google User Info:', userInfo);

        // Gửi thông tin Google về backend API
        const backendResponse = await api.post('/auth/google-login', {
          idGoogle: userInfo.sub,
          email: userInfo.email,
          name: userInfo.name,
          avatar: userInfo.picture
        });

        console.log('Backend Response:', backendResponse.data);

        // Lấy dữ liệu từ backend
        const { data: { token, user: backendUser } } = backendResponse.data;

        // Lưu token và user data
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(backendUser));
        sessionStorage.setItem('googleUser', JSON.stringify(backendUser));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(backendUser);

        // Điều hướng dựa trên trạng thái và role
        handleUserNavigation(backendUser);

      } catch (error) {
        console.error('Error:', error);
        
        // Xử lý các loại lỗi khác nhau
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;
          
          switch (status) {
            case 400:
              toast.error(data?.message || 'Dữ liệu không hợp lệ');
              break;
            case 401:
              toast.error(data?.message || 'Không có quyền truy cập');
              break;
            case 403:
              toast.error(data?.message || 'Tài khoản chưa được phê duyệt');
              break;
            case 404:
              toast.error('API không tồn tại');
              break;
            case 500:
              toast.error('Server đang gặp sự cố, vui lòng thử lại sau');
              break;
            default:
              toast.error(data?.message || 'Đăng nhập thất bại');
          }
        } else if (error.request) {
          // Network error - không thể kết nối đến server
          toast.error('Không thể kết nối đến server, vui lòng kiểm tra kết nối mạng');
        } else {
          // Lỗi khác
          toast.error('Đăng nhập thất bại');
        }
        
        throw error;
      }
    },
    onError: () => {
      toast.error('Đăng nhập Google thất bại');
    }
  });

  // Hàm xử lý điều hướng dựa trên trạng thái và role
  const handleUserNavigation = (user) => {

    console.log(user);
    if (user.status === 'waiting_approval') {
      // Nếu chưa được duyệt -> chuyển đến trang chờ duyệt
      toast.success('Tài khoản đang chờ duyệt');
      navigate('/pending-approval');
    } else if (user.status === 'approved') {
      // Nếu đã được duyệt -> kiểm tra role và điều hướng
      switch (user.role) {
        case 'admin':
          toast.success('Đăng nhập Admin thành công!');
          navigate('/admin');
          break;
        case 'seller':
          toast.success('Đăng nhập Seller thành công!');
          navigate('/seller');
          break;
        case 'designer':
          toast.success('Đăng nhập Designer thành công!');
          navigate('/designer');
          break;
        default:
          toast.success('Đăng nhập thành công!');
          navigate('/dashboard');
          break;
      }
    }
  };

  const loginWithGoogle = async () => {
    googleLogin();
  };



  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('googleUser');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Đăng xuất thành công!');
    navigate('/');
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    isApproved: user?.status === 'approved',
    isPending: user?.status === 'pending',
    isAdmin: user?.role === 'admin',
    isSeller: user?.role === 'seller',
    isDesigner: user?.role === 'designer'
  };


  
  

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 
